import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  CheckCircle2,
  AlertCircle,
  Plus,
  User,
  Package,
  Loader2,
  ChevronRight,
  BookUser,
  Archive,
} from "lucide-react";
import { navigate } from "wouter/use-browser-location";
import { useQueryClient } from "@tanstack/react-query";
import { useBuyers } from "@/hooks/useBuyers";
import { useItems } from "@/hooks/useItems";
import { useToast } from "@/hooks/use-toast";
import type { LineItemMatchResult } from "@/utils/matchingEngine";
import type {
  BuyerMatchCandidate,
  ExtractedData,
  ItemMatchCandidate,
  MatchResult,
} from "@/types/gmail";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  emailId: string;
  extractedData: ExtractedData;
  buyerMatchResult: MatchResult<BuyerMatchCandidate>;
  itemMatchResults: LineItemMatchResult[];
}

type BuyerSelectionMode = "existing" | "new";
type ItemSelectionMode = "existing" | "new";

function generateNextBuyerCode(
  existingBuyers: Array<{ code: string | null }>,
): string {
  const nums = existingBuyers
    .map((b) => b.code)
    .filter((c): c is string => !!c && /^BUYER\d+$/.test(c))
    .map((c) => parseInt(c.replace("BUYER", ""), 10));
  const next = nums.length > 0 ? Math.max(...nums) + 1 : 1;
  return `BUYER${String(next).padStart(3, "0")}`;
}

export const QuotationMatchingModal: React.FC<Props> = ({
  isOpen,
  onClose,
  emailId,
  extractedData,
  buyerMatchResult,
  itemMatchResults,
}) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { buyers, createBuyerAsync } = useBuyers();
  const { items, createItemAsync } = useItems();

  // Buyer selection state — default to "new" when no matches, "existing" when matches found
  const [buyerMode, setBuyerMode] = React.useState<BuyerSelectionMode>(
    buyerMatchResult.type !== "none" ? "existing" : "new"
  );
  const [selectedBuyerId, setSelectedBuyerId] = React.useState<string>(
    buyerMatchResult.matches[0]?.id || ""
  );
  // Default to saving new buyer when no match was found
  const [saveToBuyerMaster, setSaveToBuyerMaster] = React.useState(
    buyerMatchResult.type === "none"
  );

  // Per-item selection state
  const [itemSelections, setItemSelections] = React.useState<
    Array<{ mode: ItemSelectionMode; selectedId: string }>
  >(
    itemMatchResults.map((m) => ({
      mode: m.matchResult.type !== "none" ? "existing" : "new",
      selectedId: m.matchResult.matches[0]?.id || "",
    }))
  );
  // Default to saving new items when no match was found
  const [saveToItemMasters, setSaveToItemMasters] = React.useState<boolean[]>(
    itemMatchResults.map((m) => m.matchResult.type === "none")
  );

  const [isCreating, setIsCreating] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState("buyer");

  const rawBuyers = ((buyers?.data ?? buyers) as unknown as Array<{
    id: string;
    code: string | null;
    name: string;
    email: string;
    tin: string;
    registrationType: string;
    registrationNumber: string;
    contactNumber?: string | null;
    sstRegistrationNumber?: string | null;
    buyerAddress?: {
      addressLine0?: string | null;
      addressLine1?: string | null;
      addressLine2?: string | null;
      postalZone?: string | null;
      cityName?: string;
      state?: string;
      country?: string;
    };
  }>) || [];
  const buyerList: BuyerMatchCandidate[] = rawBuyers.map((b) => ({
    id: b.id,
    buyerCode: b.code,
    buyerName: b.name,
    buyerEmail: b.email,
    buyerTin: b.tin,
    buyerRegistrationType: b.registrationType,
    buyerRegistrationNumber: b.registrationNumber,
    buyerCity: b.buyerAddress?.cityName || "",
    buyerState: b.buyerAddress?.state || "",
    buyerCountry: b.buyerAddress?.country || "",
    similarity: 0,
  }));
  const itemList = (items?.data as unknown as ItemMatchCandidate[]) || [];

  const setItemSelection = (idx: number, update: Partial<{ mode: ItemSelectionMode; selectedId: string }>) => {
    setItemSelections((prev) =>
      prev.map((s, i) => (i === idx ? { ...s, ...update } : s))
    );
  };

  const handleCreateQuotation = async () => {
    setIsCreating(true);
    try {
      // --- Save new buyer to master if requested ---
      if (buyerMode === "new" && saveToBuyerMaster) {
        try {
          const b = extractedData.buyer;
          const rawContact = (b.buyer_contact || "").replace(/\D/g, "");
          // Keep last 10-11 digits (strip country code if present)
          const contactNumber =
            rawContact.length > 11
              ? rawContact.slice(-11)
              : rawContact.length >= 10
              ? rawContact
              : "0000000000";

          await createBuyerAsync({
            code: generateNextBuyerCode(rawBuyers),
            name: b.buyer_company_name || b.buyer_name || "Unknown Buyer",
            tin: "EI00000000010",
            email: b.buyer_email || "unknown@email.com",
            contactNumber,
            registrationType: "BRN",
            registrationNumber: "N/A",
            sstRegistrationNumber: null,
            buyerAddress: {
              addressLine0: b.buyer_address_line0 || "N/A",
              addressLine1: null,
              addressLine2: null,
              postalZone: null,
              cityName: "",
              state: "17",
              country: "MYS",
            },
          });
          queryClient.invalidateQueries({ queryKey: ["buyers"] });
          toast({
            title: "Buyer added to master",
            description: "Please update TIN and registration details in the Buyers page.",
          });
        } catch {
          toast({
            title: "Buyer not saved to master",
            description: "Could not save buyer — add them manually from the Buyers page.",
            variant: "destructive",
          });
        }
      }

      // --- Save new items to master if requested ---
      for (let idx = 0; idx < itemMatchResults.length; idx++) {
        const selection = itemSelections[idx];
        if (selection.mode === "new" && saveToItemMasters[idx]) {
          const extracted = itemMatchResults[idx].extractedItem;
          try {
            await createItemAsync({
              itemCode: extracted.product_code || `EMAIL-ITEM-${Date.now()}`,
              description: extracted.item_desc || "New Item",
              classification: "022",
              unitPrice: extracted.unit_price_myr || 0,
              taxType: "06",
              taxRate: 0,
              taxAmount: 0,
              discountRate: 0,
              feeChargeRate: 0,
              countryOfOrigin: "MYS",
              isEditable: 0,
              measurement: "",
              quantity: 0,
              weightedAverage: null,
              salesGlCode: null,
              purchaseGlCode: null,
              stockType: "non-stock",
              detailsOfTaxExemption: "",
              amountExemptedFromTax: 0,
              productTariffCode: "",
              createdAt: new Date(),
            });
            queryClient.invalidateQueries({ queryKey: ["items"] });
          } catch {
            toast({
              title: "Item not saved to master",
              description: `"${extracted.item_desc || "Item"}" could not be saved — add it manually from the Items page.`,
              variant: "destructive",
            });
          }
        }
      }

      // --- Resolve buyer data for quotation form ---
      let buyerData: Record<string, unknown> = {};
      if (buyerMode === "existing" && selectedBuyerId) {
        const buyer = buyerList.find((b) => b.id === selectedBuyerId);
        const fullBuyer = rawBuyers.find((b) => b.id === selectedBuyerId);
        if (buyer) {
          const rawContact = (fullBuyer?.contactNumber || "").replace(/[^\d+]/g, "").replace(/(?!^)\+/g, "");
          buyerData = {
            buyerCode: buyer.id,
            buyerDisplayCode: buyer.buyerCode || null,
            buyerName: buyer.buyerName,
            buyerEmail: buyer.buyerEmail,
            buyerRegistrationType: buyer.buyerRegistrationType,
            buyerRegistrationNumber: buyer.buyerRegistrationNumber,
            buyerTin: buyer.buyerTin,
            buyerContact: rawContact.length >= 8 ? rawContact : "0100000000",
            buyerAddressLine0: fullBuyer?.buyerAddress?.addressLine0 || "",
            buyerAddressLine1: fullBuyer?.buyerAddress?.addressLine1 ?? null,
            buyerAddressLine2: fullBuyer?.buyerAddress?.addressLine2 ?? null,
            buyerPostcode: fullBuyer?.buyerAddress?.postalZone ?? null,
            buyerCity: buyer.buyerCity,
            buyerState: buyer.buyerState,
            buyerCountry: buyer.buyerCountry,
            buyerSstRegistrationNumber: fullBuyer?.sstRegistrationNumber ?? null,
          };
        }
      } else {
        // New buyer from extracted data
        buyerData = {
          buyerCode: null,
          buyerName: extractedData.buyer.buyer_name || extractedData.buyer.buyer_company_name || "",
          buyerEmail: extractedData.buyer.buyer_email || "",
          buyerContact: extractedData.buyer.buyer_contact || "",
          buyerRegistrationType: "BRN",
          buyerRegistrationNumber: "",
          buyerTin: "EI00000000010",
          buyerCity: "",
          buyerState: "00",
          buyerCountry: "MYS",
          buyerAddressLine0: extractedData.buyer.buyer_address_line0 || "",
          buyerSstRegistrationNumber: null,
        };
      }

      // --- Resolve line items ---
      const lineItems = itemMatchResults.map((match, idx) => {
        const selection = itemSelections[idx];
        const extracted = match.extractedItem;

        if (selection.mode === "existing" && selection.selectedId) {
          const item = itemList.find((i) => i.id === selection.selectedId);
          if (item) {
            const qty = extracted.qty || 1;
            const unitPrice = extracted.unit_price_myr || item.unitPrice || 0;
            const taxRate = item.taxRate || 0;
            const subtotal = qty * unitPrice;
            const taxAmount = subtotal * (taxRate / 100);
            const totalExcludingTax = subtotal;
            const total = subtotal + taxAmount;

            return {
              itemCode: item.itemCode,
              classification: item.classification,
              description: extracted.item_desc || item.description,
              unitPrice,
              quantity: qty,
              measurement: item.measurement || null,
              discountRate: null,
              discountAmount: null,
              feeRate: null,
              feeAmount: null,
              taxType: item.taxType,
              taxRate,
              taxAmount,
              detailsOfTaxExemption: null,
              amountExemptedFromTax: null,
              subtotal,
              totalExcludingTax,
              total,
              productTariffCode: null,
              countryOfOrigin: null,
            };
          }
        }

        // New item from extracted data
        const qty = extracted.qty || 1;
        const unitPrice = extracted.unit_price_myr || 0;
        const subtotal = qty * unitPrice;

        return {
          itemCode: extracted.product_code || "NEW-ITEM",
          classification: "022",
          description: extracted.item_desc || "",
          unitPrice,
          quantity: qty,
          measurement: null,
          discountRate: null,
          discountAmount: null,
          feeRate: null,
          feeAmount: null,
          taxType: "01",
          taxRate: 0,
          taxAmount: 0,
          detailsOfTaxExemption: null,
          amountExemptedFromTax: null,
          subtotal,
          totalExcludingTax: subtotal,
          total: subtotal,
          productTariffCode: null,
          countryOfOrigin: null,
        };
      });

      // Calculate totals
      const totalExcludingTax = lineItems.reduce((s, i) => s + i.totalExcludingTax, 0);
      const totalTaxAmount = lineItems.reduce((s, i) => s + i.taxAmount, 0);
      const totalIncludingTax = totalExcludingTax + totalTaxAmount;

      // Navigate to QuotationForm with pre-filled data via sessionStorage
      const prefillData = {
        emailId,
        buyer: buyerData,
        lineItems,
        totalExcludingTax,
        totalTaxAmount,
        totalIncludingTax,
        totalPayableAmount: totalIncludingTax,
        isEmailGenerated: true,
        sourceEmailId: emailId,
      };

      sessionStorage.setItem("quotation_prefill", JSON.stringify(prefillData));
      onClose();
      navigate("/quotation/new?from_email=true");
    } catch {
      toast({
        title: "Error",
        description: "Failed to prepare quotation data. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  const hasExistingBuyerMatches = buyerMatchResult.matches.length > 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Quotation from Email</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Review and confirm the buyer and item matching before creating the quotation.
          </p>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="buyer" className="gap-2">
              <User className="h-4 w-4" />
              Buyer
              {buyerMode === "existing" && selectedBuyerId ? (
                <CheckCircle2 className="h-3 w-3 text-green-500" />
              ) : (
                <AlertCircle className="h-3 w-3 text-orange-400" />
              )}
            </TabsTrigger>
            <TabsTrigger value="items" className="gap-2">
              <Package className="h-4 w-4" />
              Items ({itemMatchResults.length})
            </TabsTrigger>
          </TabsList>

          {/* ============ Buyer Tab ============ */}
          <TabsContent value="buyer" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Extracted from Email</Label>
              <Card>
                <CardContent className="pt-3 pb-3 text-sm space-y-1">
                  {extractedData.buyer.buyer_company_name && (
                    <div><span className="text-muted-foreground">Company: </span>{extractedData.buyer.buyer_company_name}</div>
                  )}
                  {extractedData.buyer.buyer_name && (
                    <div><span className="text-muted-foreground">Name: </span>{extractedData.buyer.buyer_name}</div>
                  )}
                  {extractedData.buyer.buyer_email && (
                    <div><span className="text-muted-foreground">Email: </span>{extractedData.buyer.buyer_email}</div>
                  )}
                  {extractedData.buyer.buyer_contact && (
                    <div><span className="text-muted-foreground">Contact: </span>{extractedData.buyer.buyer_contact}</div>
                  )}
                  {!extractedData.buyer.buyer_company_name &&
                    !extractedData.buyer.buyer_name &&
                    !extractedData.buyer.buyer_email &&
                    !extractedData.buyer.buyer_contact && (
                    <p className="text-muted-foreground text-xs italic">No buyer info extracted from this email.</p>
                  )}
                </CardContent>
              </Card>
            </div>

            <div className="space-y-3">
              <Label className="text-sm font-medium">Match Action</Label>
              <div className="grid grid-cols-2 gap-3">
                {/* Use Existing — disabled when no matches */}
                <Card
                  className={`border-2 transition-colors ${
                    !hasExistingBuyerMatches
                      ? "border-muted opacity-50 cursor-not-allowed bg-muted/30"
                      : buyerMode === "existing"
                      ? "border-primary cursor-pointer"
                      : "border-muted cursor-pointer"
                  }`}
                  onClick={() => hasExistingBuyerMatches && setBuyerMode("existing")}
                >
                  <CardContent className="pt-3 pb-3 text-sm">
                    <p className="font-medium">Use Existing Buyer</p>
                    <p className="text-xs text-muted-foreground">
                      {hasExistingBuyerMatches
                        ? `${buyerMatchResult.matches.length} match(es) found`
                        : "No matches found"}
                    </p>
                  </CardContent>
                </Card>
                <Card
                  className={`cursor-pointer border-2 transition-colors ${buyerMode === "new" ? "border-primary" : "border-muted"}`}
                  onClick={() => setBuyerMode("new")}
                >
                  <CardContent className="pt-3 pb-3 text-sm">
                    <div className="flex items-center gap-2">
                      <Plus className="h-4 w-4" />
                      <p className="font-medium">Create New Buyer</p>
                    </div>
                    <p className="text-xs text-muted-foreground">Fill details on quotation form</p>
                  </CardContent>
                </Card>
              </div>

              {buyerMode === "existing" && hasExistingBuyerMatches && (
                <div className="space-y-2">
                  <Label className="text-sm">Select Buyer</Label>
                  <Select value={selectedBuyerId} onValueChange={setSelectedBuyerId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a buyer..." />
                    </SelectTrigger>
                    <SelectContent>
                      {buyerMatchResult.matches.map((b) => (
                        <SelectItem key={b.id} value={b.id}>
                          {b.buyerName}
                          {b.similarity < 1 && (
                            <span className="text-muted-foreground ml-2 text-xs">
                              ({Math.round(b.similarity * 100)}% match)
                            </span>
                          )}
                        </SelectItem>
                      ))}
                      {/* Show remaining buyers */}
                      {buyerList
                        .filter((b) => !buyerMatchResult.matches.find((m) => m.id === b.id))
                        .map((b) => (
                          <SelectItem key={b.id} value={b.id}>
                            {b.buyerName}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Save to buyer master checkbox */}
              {buyerMode === "new" && (
                <div className="rounded-md border border-dashed p-3 space-y-1">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="save-buyer-master"
                      checked={saveToBuyerMaster}
                      onCheckedChange={(checked) => setSaveToBuyerMaster(!!checked)}
                    />
                    <label
                      htmlFor="save-buyer-master"
                      className="text-sm font-medium cursor-pointer flex items-center gap-1.5"
                    >
                      <BookUser className="h-4 w-4 text-muted-foreground" />
                      Also save to Buyer Master
                    </label>
                  </div>
                  {saveToBuyerMaster && (
                    <p className="text-xs text-muted-foreground pl-6">
                      Buyer will be created with extracted info. TIN and registration details will need to be completed in the Buyers page.
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-end">
              <Button onClick={() => setActiveTab("items")} className="gap-2">
                Next: Items <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </TabsContent>

          {/* ============ Items Tab ============ */}
          <TabsContent value="items" className="space-y-4 mt-4">
            {itemMatchResults.length === 0 && (
              <div className="text-sm text-muted-foreground text-center py-6">
                No line items were extracted from this email.
                <br />
                You can add items manually on the quotation form.
              </div>
            )}

            {itemMatchResults.map((match, idx) => {
              const selection = itemSelections[idx];
              const extracted = match.extractedItem;
              const hasItemMatches = match.matchResult.matches.length > 0;

              return (
                <Card key={idx} className="border">
                  <CardContent className="pt-4 space-y-3">
                    <div className="flex items-start justify-between">
                      <div className="text-sm">
                        <p className="font-medium">{extracted.item_desc || "Item " + (idx + 1)}</p>
                        <p className="text-xs text-muted-foreground">
                          Qty: {extracted.qty || "—"} &middot; Price: MYR {extracted.unit_price_myr || "—"}
                        </p>
                      </div>
                      <Badge
                        variant={match.matchResult.type === "exact" ? "default" : match.matchResult.type === "fuzzy" ? "outline" : "secondary"}
                        className="text-xs"
                      >
                        {match.matchResult.type === "exact" ? "Exact Match" : match.matchResult.type === "fuzzy" ? `${match.matchResult.matches.length} Matches` : "No Match"}
                      </Badge>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <Card
                        className={`border-2 transition-colors ${
                          !hasItemMatches
                            ? "border-muted opacity-50 cursor-not-allowed bg-muted/30"
                            : selection.mode === "existing"
                            ? "border-primary cursor-pointer"
                            : "border-muted cursor-pointer"
                        }`}
                        onClick={() => hasItemMatches && setItemSelection(idx, { mode: "existing" })}
                      >
                        <CardContent className="pt-2 pb-2 text-xs">
                          <p className="font-medium">Use Existing Item</p>
                          {!hasItemMatches && (
                            <p className="text-muted-foreground">No matches found</p>
                          )}
                        </CardContent>
                      </Card>
                      <Card
                        className={`cursor-pointer border-2 transition-colors ${selection.mode === "new" ? "border-primary" : "border-muted"}`}
                        onClick={() => setItemSelection(idx, { mode: "new" })}
                      >
                        <CardContent className="pt-2 pb-2 text-xs">
                          <div className="flex items-center gap-1">
                            <Plus className="h-3 w-3" />
                            <p className="font-medium">Create New Item</p>
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {selection.mode === "existing" && hasItemMatches && (
                      <Select
                        value={selection.selectedId}
                        onValueChange={(v) => setItemSelection(idx, { selectedId: v })}
                      >
                        <SelectTrigger className="h-8 text-sm">
                          <SelectValue placeholder="Select item..." />
                        </SelectTrigger>
                        <SelectContent>
                          {match.matchResult.matches.map((item) => (
                            <SelectItem key={item.id} value={item.id}>
                              [{item.itemCode}] {item.description}
                              {item.similarity < 1 && (
                                <span className="text-muted-foreground ml-2 text-xs">
                                  ({Math.round(item.similarity * 100)}%)
                                </span>
                              )}
                            </SelectItem>
                          ))}
                          {itemList
                            .filter((i) => !match.matchResult.matches.find((m) => m.id === i.id))
                            .map((item) => (
                              <SelectItem key={item.id} value={item.id}>
                                [{item.itemCode}] {item.description}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    )}

                    {/* Save to item master checkbox */}
                    {selection.mode === "new" && (
                      <div className="rounded-md border border-dashed p-2 space-y-1">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id={`save-item-master-${idx}`}
                            checked={saveToItemMasters[idx]}
                            onCheckedChange={(checked) =>
                              setSaveToItemMasters((prev) =>
                                prev.map((v, i) => (i === idx ? !!checked : v))
                              )
                            }
                          />
                          <label
                            htmlFor={`save-item-master-${idx}`}
                            className="text-xs font-medium cursor-pointer flex items-center gap-1"
                          >
                            <Archive className="h-3.5 w-3.5 text-muted-foreground" />
                            Also save to Item Master
                          </label>
                        </div>
                        {saveToItemMasters[idx] && (
                          <p className="text-xs text-muted-foreground pl-6">
                            Item will be created with extracted description and price. Tax type, classification, and country will need to be completed in the Items page.
                          </p>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </TabsContent>
        </Tabs>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={isCreating}>
            Cancel
          </Button>
          <Button onClick={handleCreateQuotation} disabled={isCreating} className="gap-2">
            {isCreating ? <Loader2 className="h-4 w-4 animate-spin" /> : <ChevronRight className="h-4 w-4" />}
            Continue to Quotation Form
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
